import { Controller } from "@hotwired/stimulus"

// Polls the existing render_jobs/:id.json endpoint while a curation render is in
// flight and reflects status/progress/output onto the render panel — so
// "Queued…" flips to "Rendering… 42%" and finally to a "View / Download" link
// without a manual refresh. JSON-based sibling of voiceover_status_controller.
//
// The JSON payload (RenderJobsController#render_job_json) supplies:
//   { status, progress (0-100), output_url, ... }
// Terminal states stop the poller. maxAttempts bounds a left-open tab.
export default class extends Controller {
  static targets = ["badge", "progressBar", "progressLabel", "link"]
  static values = {
    url: String,
    status: String,
    interval: { type: Number, default: 3000 },
    maxAttempts: { type: Number, default: 300 },
  }

  static TERMINAL = ["completed", "failed", "cancelled"]

  connect() {
    if (!this.isTerminal(this.statusValue)) this.startPolling()
  }

  disconnect() {
    this.stopPolling()
  }

  startPolling() {
    this.attempts = 0
    this.timer = setInterval(() => this.poll(), this.intervalValue)
  }

  stopPolling() {
    if (this.timer) clearInterval(this.timer)
    this.timer = null
  }

  isTerminal(status) {
    return this.constructor.TERMINAL.includes(status)
  }

  async poll() {
    if (++this.attempts > this.maxAttemptsValue) return this.stopPolling()

    let data
    try {
      const res = await fetch(this.urlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
      if (!res.ok) return // transient — keep polling
      data = await res.json()
    } catch (_e) {
      return // transient network error — keep polling
    }

    this.render(data)
    if (this.isTerminal(data.status)) this.stopPolling()
  }

  render(data) {
    const status = data.status
    this.statusValue = status

    if (this.hasBadgeTarget) {
      this.badgeTarget.textContent = this.label(status)
      this.badgeTarget.dataset.status = status
    }

    const progress = Number(data.progress) || 0
    if (this.hasProgressBarTarget) this.progressBarTarget.style.width = `${progress}%`
    if (this.hasProgressLabelTarget) this.progressLabelTarget.textContent = `${progress}%`

    if (this.hasLinkTarget && status === "completed" && data.output_url) {
      this.linkTarget.setAttribute("href", data.output_url)
      this.linkTarget.hidden = false
    }
  }

  label(status) {
    switch (status) {
      case "pending": return "Queued…"
      case "processing": return "Rendering…"
      case "completed": return "Completed"
      case "failed": return "Failed"
      case "cancelled": return "Cancelled"
      default: return status
    }
  }
}
